import { useState } from "react";
import ThemeToggle from "./ThemeToggle";
import ThemeChoiceDialog from "./ThemeChoiceDialog";
import { Link, Outlet, Navigate } from "react-router-dom";
import { LogOut, UserPlus, Check, ChevronDown, Shield, Bell, Loader2 } from "lucide-react";
import "../styles/oss-theme.css";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useOssAuth } from "../context/OssAuthContext";
import OssResponsabilePasswordDialog from "./oss/OssResponsabilePasswordDialog";
import {
  OssPushNotificationPrompt,
  OSS_PUSH_OPTIONS,
  usePushNotifications,
} from "./PushNotifications";
import { useOssNativeShell } from "./oss/useOssNativeShell";

const LOGO_MARK_URL = "/images/logo-oss-mark.png";
const LOGO_LOCKUP_URL = "/images/logo-oss.png";

const AVATAR_COLORS = [
  "bg-purple-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-blue-500",
  "bg-amber-500",
  "bg-sky-500",
];

function avatarColor(id = "") {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(s) {
  return `${s?.nome?.[0] ?? ""}${s?.cognome?.[0] ?? ""}`.toUpperCase() || "?";
}

function roleLabel(session) {
  return session?.responsabile ? "OSS Responsabile" : "OSS";
}

function OssAvatar({ session, size = "md" }) {
  const sz = size === "sm" ? "w-7 h-7 text-xs" : "w-9 h-9 text-sm";
  return (
    <div
      className={`${sz} rounded-full ${avatarColor(session?.id)} flex items-center justify-center text-white font-semibold shrink-0`}
    >
      {getInitials(session)}
    </div>
  );
}

function OssPushMenuItem({ token }) {
  const { supported, configured, subscribed, loading, subscribe, unsubscribe } =
    usePushNotifications(token, OSS_PUSH_OPTIONS);

  if (!supported) return null;

  return (
    <DropdownMenuItem
      className="flex items-center gap-2 cursor-pointer text-slate-700"
      onSelect={(e) => {
        e.preventDefault();
        if (!configured || loading) return;
        if (subscribed) unsubscribe();
        else subscribe();
      }}
      disabled={loading || !configured}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 text-slate-500 shrink-0 animate-spin" />
      ) : (
        <Bell className="w-4 h-4 text-slate-500 shrink-0" />
      )}
      <span className="flex-1">
        {subscribed ? "Disattiva avvisi terapie" : "Attiva avvisi terapie"}
      </span>
      {subscribed ? (
        <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
          On
        </span>
      ) : null}
    </DropdownMenuItem>
  );
}

function OssLayoutShell() {
  useOssNativeShell();
  const { activeOss, sessions, loading, logoutActive, switchTo, addAnotherUser, token } =
    useOssAuth();
  const [switchingId, setSwitchingId] = useState(null);
  const [passwordTarget, setPasswordTarget] = useState(null);

  if (loading) {
    return (
      <div
        className="oss-shell min-h-screen flex items-center justify-center"
        style={{ minHeight: "100dvh" }}
      >
        <div role="status" aria-label="Caricamento" className="w-10 h-10 border-[3px] border-sky-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!activeOss) {
    return <Navigate to="/oss-login" replace />;
  }

  const otherSessions = sessions.filter((s) => s.id !== activeOss.id);
  const isResponsabile = Boolean(activeOss.responsabile);

  const handleSwitch = async (id) => {
    setSwitchingId(id);
    try {
      const result = await switchTo(id);
      if (result?.needsPassword) {
        setPasswordTarget(result.session);
      }
    } finally {
      setSwitchingId(null);
    }
  };

  const handlePasswordConfirm = async (password) => {
    if (!passwordTarget?.id) return { ok: false };
    return switchTo(passwordTarget.id, { password });
  };

  return (
    <div className="oss-shell relative min-h-screen print:min-h-0 print:bg-white" style={{ minHeight: "100dvh" }}>
      <ThemeChoiceDialog />
      {/* Sfondo calmo: una sola sfumatura leggera, niente motivi decorativi */}
      <div aria-hidden className="oss-ambient-bg pointer-events-none fixed inset-0 z-0 overflow-hidden print:hidden">
        <div
          className="absolute inset-x-0 top-0 h-[420px]"
          style={{ background: "radial-gradient(80% 100% at 85% 0%, rgba(184, 216, 238, 0.45) 0%, rgba(243, 246, 247, 0) 70%)" }}
        />
      </div>

      <div className="relative z-10">
        {/* Header essenziale: logo · ruolo · utente · tema · esci */}
        <div className="sticky top-0 z-50 px-3 sm:px-5 pt-3 pb-2 print:hidden">
          <header className="oss-header-bar mx-auto max-w-7xl w-full">
            <div className="flex h-16 items-center justify-between pl-3 pr-2 sm:pl-4 gap-3">
              <Link to="/oss" aria-label="Dashboard OSS" className="flex items-center gap-2 min-w-0 shrink-0 min-h-[44px]">
                <img src={LOGO_MARK_URL} alt="" className="h-9 w-9 rounded-xl object-contain sm:hidden" />
                <img src={LOGO_LOCKUP_URL} alt="OSS by Affidya" className="hidden h-8 w-auto sm:block" />
              </Link>

              <div className="flex items-center gap-1.5">
                {isResponsabile && (
                  <span className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-300/60">
                    <Shield className="w-3.5 h-3.5" aria-hidden />
                    Responsabile
                  </span>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="oss-icon-btn flex items-center gap-2.5 px-2 sm:pl-2 sm:pr-3 touch-manipulation" aria-label="Menu utente">
                      <OssAvatar session={activeOss} />
                      <div className="hidden sm:block text-left min-w-0">
                        <p className="text-sm font-semibold oss-text leading-tight truncate max-w-[160px]">
                          {activeOss.nome} {activeOss.cognome?.[0]}.
                        </p>
                        <p
                          className={`text-xs leading-tight ${
                            isResponsabile ? "text-amber-700 font-medium" : "text-slate-500"
                          }`}
                        >
                          {roleLabel(activeOss)}
                        </p>
                      </div>
                      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel className="text-xs text-slate-400 font-medium uppercase tracking-wide pb-1">
                      Utente attivo
                    </DropdownMenuLabel>
                    <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-slate-50 mx-1 mb-1">
                      <OssAvatar session={activeOss} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {activeOss.nome} {activeOss.cognome}
                        </p>
                        <p
                          className={`text-xs ${
                            isResponsabile ? "text-amber-700 font-medium" : "text-slate-500"
                          }`}
                        >
                          {roleLabel(activeOss)}
                        </p>
                      </div>
                      <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    </div>

                    {otherSessions.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-slate-400 font-medium uppercase tracking-wide pb-1 pt-2">
                          Altri utenti
                        </DropdownMenuLabel>
                        {otherSessions.map((s) => (
                          <DropdownMenuItem
                            key={s.id}
                            className="flex items-center gap-3 cursor-pointer py-2"
                            onClick={() => handleSwitch(s.id)}
                            disabled={switchingId === s.id}
                          >
                            <OssAvatar session={s} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">
                                {s.nome} {s.cognome}
                              </p>
                              <p
                                className={`text-xs ${
                                  s.responsabile ? "text-amber-700 font-medium" : "text-slate-500"
                                }`}
                              >
                                {roleLabel(s)}
                                {s.responsabile ? " · password" : ""}
                              </p>
                            </div>
                            {switchingId === s.id && (
                              <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin shrink-0" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}

                    <DropdownMenuSeparator />
                    <OssPushMenuItem token={token} />
                    <DropdownMenuItem
                      className="flex items-center gap-2 cursor-pointer text-slate-700"
                      onClick={addAnotherUser}
                    >
                      <UserPlus className="w-4 h-4 text-slate-500 shrink-0" />
                      Accedi con un altro utente
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <ThemeToggle className="h-11 w-11 rounded-[0.875rem]" />
                <span aria-hidden className="mx-1 hidden sm:block h-6 w-px bg-slate-200" />
                <button
                  type="button"
                  onClick={logoutActive}
                  className="oss-icon-btn oss-icon-btn--outline px-3 text-sm font-medium touch-manipulation shrink-0"
                >
                  <LogOut className="w-4 h-4" aria-hidden />
                  <span className="hidden sm:inline">Esci</span>
                  <span className="sr-only sm:hidden">Esci</span>
                </button>
              </div>
            </div>
          </header>
        </div>

        <main className="max-w-7xl mx-auto w-full px-3 sm:px-5 pt-2 pb-8 print:max-w-none print:p-6 print:py-4">
          <Outlet />
        </main>
      </div>

      <OssResponsabilePasswordDialog
        open={Boolean(passwordTarget)}
        session={passwordTarget}
        onClose={() => setPasswordTarget(null)}
        onConfirm={handlePasswordConfirm}
      />

      <OssPushNotificationPrompt token={token} />
    </div>
  );
}

export default function OssLayout() {
  return <OssLayoutShell />;
}
